import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  FileText,
  Gavel,
  Lock,
  Scale,
  Search,
  Sparkles,
  Upload,
  Workflow,
  Zap,
} from 'lucide-react';
import { useRevealOnScroll } from '../../../hooks/useRevealOnScroll';
import {
  FEATURE_LITIGATION,
  ORBIS_LITIGATION_FEATURE,
  PRODUCT_PREMIUM,
  ROUTE_PRODUCT_PREMIUM,
  TARIF_PREMIUM,
} from '../../../constants';
import { T05PricingSection } from '../../landing-templates/t05-launchdarkly/T05PricingSection';
import { buildLitigationPricingPlans } from '../../landing-templates/t05-launchdarkly/t05PricingPlans';
import { T05ProductShell } from './T05ProductShell';
import { T05ReferoFeatureRow } from './T05ReferoFeatureRow';
import { navigateToHomePricing } from '../../../lib/t05LeadCta';
import { T05HeroLeadForm } from '../../landing-templates/t05-launchdarkly/T05HeroLeadForm';
import { T05LeadModal } from '../../landing-templates/t05-launchdarkly/T05LeadModal';
import { useT05ProductLead } from '../../../lib/t05ProductLead';
import type { T05ProductPageProps } from './routes.fragment';
import './t05-product.css';

const LITIGATION_LEAD = { interest: 'Orbis Premium – Litigation Engine', quelle: 't05-product-litigation' };

interface Props extends T05ProductPageProps {}

const PIPELINE = [
  { label: 'Schriftsatz-Upload', icon: Upload },
  { label: 'Pseudonymisierung', icon: Lock },
  { label: 'KI-Analyse', icon: Zap },
  { label: 'Strategie-PDF', icon: FileText },
] as const;

function LitigationPipelineVisual() {
  return (
    <div className="t05-prod-screen" aria-label="Litigation-Pipeline">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">litigation://pipeline</span>
      </div>
      <div className="t05-prod-screen__body">
        <div className="t05-prod-pipeline" aria-label="Litigation-Pipeline Schritte">
          {PIPELINE.map((step, index) => {
            const Icon = step.icon;
            return (
              <React.Fragment key={step.label}>
                <div className="t05-prod-pipeline__step">
                  <div className="t05-prod-pipeline__icon">
                    <Icon size={18} aria-hidden />
                  </div>
                  <span className="t05-prod-pipeline__label">{step.label}</span>
                </div>
                {index < PIPELINE.length - 1 && (
                  <div className="t05-prod-pipeline__connector" aria-hidden />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function LitigationAnalysisScreen() {
  return (
    <div className="t05-product-panel t05-product-panel--stack" aria-label="Litigation-Analyse — Platzhalter">
      <div className="t05-product-panel__bar" aria-hidden>
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
      </div>
      <div className="t05-product-panel__title">litigation://analyse</div>
      <div className="t05-product-panel__grid">
        <div className="t05-product-panel__row">
          <span>Gegner_Schriftsatz.pdf</span>
          <span className="t05-product-panel__badge">Upload</span>
        </div>
        <div className="t05-product-panel__row">
          <span>Pseudonymisiert · Opt-In</span>
          <span className="t05-product-panel__badge">Schicht 1+2</span>
        </div>
        <div className="t05-product-panel__row">
          <span>Strategie-Entwurf_Weber.pdf</span>
          <span className="t05-product-panel__badge">PDF</span>
        </div>
      </div>
    </div>
  );
}

function PseudonymizationScreen() {
  return (
    <div className="t05-prod-screen" aria-label="Doppelte Pseudonymisierung">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">litigation://pseudonymisierung</span>
      </div>
      <div className="t05-prod-screen__body">
        <div className="t05-prod-pseudo-grid">
          <div className="t05-prod-addon">
            <span className="t05-prod-addon__price">1</span>
            <div>
              <p className="t05-prod-addon__title">Sie anonymisieren</p>
              <p className="t05-prod-addon__desc">
                Namen und Firmen werden durch Codes ersetzt — Mandant A, Firma B, Objekt X.
              </p>
            </div>
          </div>
          <div className="t05-prod-addon">
            <span className="t05-prod-addon__price">2</span>
            <div>
              <p className="t05-prod-addon__title">ARKORBIT pseudonymisiert</p>
              <p className="t05-prod-addon__desc">
                Vor dem API-Versand ersetzt ARKORBIT verbleibende Bezeichner durch technische Codes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const ProductLitigation: React.FC<Props> = ({ onLoginClick, onLeadSubmit }) => {
  useRevealOnScroll();

  const lead = useT05ProductLead({ onLeadSubmit }, LITIGATION_LEAD);
  const openDemo = () => lead.openDemo(LITIGATION_LEAD.interest);
  const goHomePricing = () => navigateToHomePricing();

  return (
    <T05ProductShell
      productLabel={FEATURE_LITIGATION}
      navAnchors={[
        { href: '#pipeline', label: 'Pipeline' },
        { href: '#sicherheit', label: 'Sicherheit' },
        { href: '#preis', label: 'Preis' },
      ]}
      onLoginClick={onLoginClick}
      onDemoClick={openDemo}
    >
      <header className="t05-hero t05-hero--product t05-hero--compact">
        <p className="t05-eyebrow reveal-up">
          <Sparkles size={14} className="inline-block mr-1" aria-hidden />
          {PRODUCT_PREMIUM} · Beta auf Anfrage
        </p>
        <h1 className="t05-headline t05-headline--product reveal-up">
          {FEATURE_LITIGATION}.
          <span className="t05-headline__accent t05-accent--litigation"> Strategie auf Kanzlei-Niveau.</span>
        </h1>
        <p className="t05-lead reveal-up">
          Gegnerischen Schriftsatz hochladen, strukturierte KI-Analyse und Strategie-PDF — mit doppelter
          Pseudonymisierung und Opt-In je Analyse. Teil von {TARIF_PREMIUM}.
        </p>
        <T05HeroLeadForm lead={lead} />
      </header>

      <section className="t05-container t05-refero-features" aria-label="Litigation Funktionen">
        <T05ReferoFeatureRow
          id="pipeline"
          accent="coral"
          label="Pipeline"
          labelIcon={Workflow}
          title="Vom Upload zum Strategie-PDF"
          description={ORBIS_LITIGATION_FEATURE.desc}
          bullets={[
            { icon: Upload, text: 'Schriftsatz-Upload — gegnerisches Dokument hochladen' },
            { icon: Lock, text: 'Doppelte Pseudonymisierung vor jeder Analyse' },
            { icon: Zap, text: 'KI-Analyse mit strukturiertem Output' },
            { icon: FileText, text: 'Strategie-PDF als Entwurf zur anwaltlichen Prüfung' },
          ]}
          footer={
            <p className="t05-prod-footnote" style={{ marginTop: 0, textAlign: 'left' }}>
              Opt-In je Analyse · Kein Training · Keine Datenspeicherung außerhalb Ihrer Session
            </p>
          }
          visual={<LitigationPipelineVisual />}
        />

        <T05ReferoFeatureRow
          reverse
          accent="voltage"
          label="Strategische Fallarbeit"
          labelIcon={Gavel}
          title={
            <>
              Argumente <em>strukturieren</em>
            </>
          }
          description="Die Litigation Engine bereitet Schriftsätze strukturiert auf — als Entwurf und Analyse, die der Anwalt prüft und verantwortet. Kein Ersatz für anwaltliche Beurteilung."
          bullets={[
            { icon: Search, text: 'Recherche & Judikatur — LEXCORE-quellengeprüft' },
            { icon: FileText, text: 'Schriftsatz-Analyse: Argumente, Schwachstellen, Klauseln' },
            { icon: Scale, text: 'Argumentationsbäume als Vorbereitung vor Verhandlungen' },
          ]}
          visual={<LitigationAnalysisScreen />}
        />

        <T05ReferoFeatureRow
          id="sicherheit"
          accent="trust"
          label="Sicherheit"
          labelIcon={Lock}
          title="Doppelte Pseudonymisierung"
          description="Zwei unabhängige Schichten schützen Mandantendaten — die erste liegt bei Ihnen, die zweite vor dem API-Versand. Anthropic sieht keine echten Kanzleidaten."
          bullets={[
            { icon: Check, text: 'Sie anonymisieren: Namen und Firmen durch Codes ersetzen' },
            { icon: Check, text: 'ARKORBIT pseudonymisiert verbleibende Bezeichner vor dem Versand' },
            { icon: Lock, text: 'Opt-In je Analyse — kein Training, keine Speicherung außerhalb der Session' },
          ]}
          visual={<PseudonymizationScreen />}
        />
      </section>

      <section className="t05-prod-section--tight t05-container reveal-up">
        <div className="t05-prod-disclaimer">
          <strong>Beta-Hinweis:</strong> Die {FEATURE_LITIGATION} ist Teil von {TARIF_PREMIUM} und wird derzeit als
          Pilot/Closed Beta auf Anfrage ausgerollt. Formulierung Website: „Nach Stand der Technik pseudonymisiert" — keine
          absolute Garantie vollständiger Anonymisierung. KI-Inhalte ersetzen keine anwaltliche Beratung.
        </div>
      </section>

      <T05PricingSection
        id="preis"
        headerStyle="section"
        className="t05-pricing--duo t05-pricing--litigation"
        kicker="Preis"
        title="In Premium enthalten. Nachkauf möglich."
        subtitle={`${FEATURE_LITIGATION} ist Teil von ${TARIF_PREMIUM} — mit Inklusiv-Kontingent laut Vertrag. Zusätzliche Analysen als Paket nachkaufbar.`}
        plans={buildLitigationPricingPlans({ onPricing: goHomePricing })}
        footnote="Inklusiv-Analysen verfallen monatlich · Nachkauf-Guthaben rollforward bis 24 Monate ab Kaufdatum."
      />

      <section className="t05-final-cta t05-container">
        <div className="t05-final-cta__band reveal-up">
          <div className="t05-final-cta__inner">
            <h2 className="t05-title">Strategie statt Bauchgefühl?</h2>
            <p className="t05-subtitle">
              Kostenfreies Analyse-Gespräch — wir zeigen Pipeline, Pseudonymisierung und Beta-Zugang.
            </p>
            <div className="t05-cta-actions">
              <button type="button" className="t05-btn t05-btn--primary" onClick={openDemo}>
                {PRODUCT_PREMIUM} anfragen <ArrowRight size={18} aria-hidden />
              </button>
              <Link to={ROUTE_PRODUCT_PREMIUM} className="t05-btn t05-btn--ghost">
                Tarifseite ansehen
              </Link>
            </div>
          </div>
        </div>
      </section>
      <T05LeadModal lead={lead} />
    </T05ProductShell>
  );
};
